import {Code, ConnectError} from '@connectrpc/connect';

export type AuthErrorOperation = | 'sign-in' | 'otp-request' | 'camera' | 'deferred-join' | 'session-join' | 'sign-out';

export type AuthErrorCategory = | 'age_restricted' | 'cancelled' | 'device_storage' | 'invalid_credentials'
  | 'invalid_request' | 'profile_unavailable' | 'rate_limited' | 'session_not_found' | 'service_unavailable'
  | 'unsupported' | 'unknown';

export type AuthErrorResolution = {
  category: AuthErrorCategory, code: string, operation: AuthErrorOperation, retryable: boolean, userMessage: string
};

type SafeAuthErrorOptions = Omit<AuthErrorResolution, 'operation'>;

export const AUTH_ERROR_MESSAGES = {
  ageRestricted: 'You have successfully completed registration. However, you must be 18 or older to play.',
  authenticationCancelled: 'Authentication was cancelled. Please try again.',
  authenticationFailed: 'Authentication failed. Please try again.',
  cameraFailed: 'Camera error. Please try again, or use OTP instead.',
  invalidCredentials: 'Your sign-in link or code is no longer valid. Please try again.',
  invalidRequest: 'We could not verify this sign-in request. Please scan the QR code again.',
  noActiveSession: 'No active simulator session was found. Please scan the QR code displayed on the simulator to connect.',
  otpInvalidRequest: 'We could not send a code to those contact details. Please check them and try again.',
  otpRateLimited: 'Too many code requests. Please wait and try again.',
  otpUnavailable: 'We could not send a code right now. Please try again.',
  profileUnavailable: 'We could not load your player profile. Please try again.',
  serviceUnavailable: 'The sign-in service is temporarily unavailable. Please try again in a moment.',
  unsupported: 'This sign-in option is temporarily unavailable. Please use another method or try again later.',
} as const;

/**
 * Error contract allowed to cross the authentication boundary. It deliberately
 * carries no provider message, metadata, headers, details, or cause.
 */
export class SafeAuthError extends Error {
  readonly category: AuthErrorCategory;
  readonly code: string;
  readonly retryable: boolean;

  constructor({category, code, retryable, userMessage}: SafeAuthErrorOptions) {
    super(userMessage);
    this.name = 'SafeAuthError';
    this.category = category;
    this.code = code;
    this.retryable = retryable;
  }
}

const connectCodeName = (code: Code): string => {
  switch (code) {
    case Code.Canceled:
      return 'canceled';
    case Code.Unknown:
      return 'unknown';
    case Code.InvalidArgument:
      return 'invalid_argument';
    case Code.DeadlineExceeded:
      return 'deadline_exceeded';
    case Code.NotFound:
      return 'not_found';
    case Code.AlreadyExists:
      return 'already_exists';
    case Code.PermissionDenied:
      return 'permission_denied';
    case Code.ResourceExhausted:
      return 'resource_exhausted';
    case Code.FailedPrecondition:
      return 'failed_precondition';
    case Code.Aborted:
      return 'aborted';
    case Code.OutOfRange:
      return 'out_of_range';
    case Code.Unimplemented:
      return 'unimplemented';
    case Code.Internal:
      return 'internal';
    case Code.Unavailable:
      return 'unavailable';
    case Code.DataLoss:
      return 'data_loss';
    case Code.Unauthenticated:
      return 'unauthenticated';
    default:
      return 'unknown';
  }
};

// qb.smg.Error predates Connect status errors and carries package-scoped
// numeric codes in successful RPC responses. Keep the small auth-facing subset
// here until the response contract moves to a generated enum or Connect codes.
const SMG_AUTH_ERROR_CODES = {
  userNotFound: 20020016, genericError: 20020043, userUnderaged: 20020051, permissionDenied: 20020055,
  invalidSmsCode: 20020056, userAccountLocked: 20020064, sessionNotFound: 20020076, smsDestinationBlocked: 20020081,
  otpRateLimited: 20020082, otpLockedOut: 20020083
} as const;

const extractNumericErrorCode = (error: unknown): number | null => {
  if (!error || typeof error !== 'object' || !('code' in error)) return null;

  const code = error.code;
  if (typeof code === 'bigint') {
    const numericCode = Number(code);
    return Number.isSafeInteger(numericCode) ? numericCode : null;
  }

  return typeof code === 'number' && Number.isSafeInteger(code) ? code : null;
};

const operationMessage = (operation: AuthErrorOperation, category: AuthErrorCategory): string => {
  if (operation === 'camera') return AUTH_ERROR_MESSAGES.cameraFailed;

  if (operation === 'otp-request') {
    if (category === 'invalid_request') return AUTH_ERROR_MESSAGES.otpInvalidRequest;
    if (category === 'rate_limited') return AUTH_ERROR_MESSAGES.otpRateLimited;
    return AUTH_ERROR_MESSAGES.otpUnavailable;
  }

  switch (category) {
    case 'age_restricted':
      return AUTH_ERROR_MESSAGES.ageRestricted;
    case 'cancelled':
      return AUTH_ERROR_MESSAGES.authenticationCancelled;
    case 'invalid_credentials':
      return AUTH_ERROR_MESSAGES.invalidCredentials;
    case 'invalid_request':
      return AUTH_ERROR_MESSAGES.invalidRequest;
    case 'rate_limited':
      return 'Too many sign-in attempts. Please wait and try again.';
    case 'session_not_found':
      return AUTH_ERROR_MESSAGES.noActiveSession;
    case 'service_unavailable':
      return AUTH_ERROR_MESSAGES.serviceUnavailable;
    case 'unsupported':
      return AUTH_ERROR_MESSAGES.unsupported;
    default:
      return AUTH_ERROR_MESSAGES.authenticationFailed;
  }
};

const classifySmgErrorCode = (code: number, operation: AuthErrorOperation): { category: AuthErrorCategory, retryable: boolean } => {
  switch (code) {
    case SMG_AUTH_ERROR_CODES.userUnderaged:
      return {category: 'age_restricted', retryable: false};
    case SMG_AUTH_ERROR_CODES.userNotFound:
      // OTP request copy must not confirm whether an account exists.
      return operation === 'otp-request'
        ? {category: 'invalid_request', retryable: false}
        : {category: 'invalid_credentials', retryable: false};
    case SMG_AUTH_ERROR_CODES.permissionDenied:
    case SMG_AUTH_ERROR_CODES.invalidSmsCode:
    case SMG_AUTH_ERROR_CODES.userAccountLocked:
    case SMG_AUTH_ERROR_CODES.otpLockedOut:
      return {category: 'invalid_credentials', retryable: false};
    case SMG_AUTH_ERROR_CODES.sessionNotFound:
      return {category: 'session_not_found', retryable: false};
    case SMG_AUTH_ERROR_CODES.smsDestinationBlocked:
      return {category: 'invalid_request', retryable: false};
    case SMG_AUTH_ERROR_CODES.otpRateLimited:
      return {category: 'rate_limited', retryable: true};
    case SMG_AUTH_ERROR_CODES.genericError:
      return {category: 'service_unavailable', retryable: true};
    default:
      return {category: 'unknown', retryable: true};
  }
};

const classifyConnectCode = (code: Code): { category: AuthErrorCategory; retryable: boolean } => {
  switch (code) {
    case Code.Canceled:
    case Code.Aborted:
      return {category: 'cancelled', retryable: true};
    case Code.InvalidArgument:
    case Code.FailedPrecondition:
    case Code.OutOfRange:
      return {category: 'invalid_request', retryable: false};
    case Code.NotFound:
      return {category: 'session_not_found', retryable: false};
    case Code.PermissionDenied:
    case Code.Unauthenticated:
      return {category: 'invalid_credentials', retryable: false};
    case Code.ResourceExhausted:
      return {category: 'rate_limited', retryable: true};
    case Code.DeadlineExceeded:
    case Code.Unavailable:
      return {category: 'service_unavailable', retryable: true};
    case Code.Unimplemented:
      return {category: 'unsupported', retryable: false};
    default:
      return {category: 'unknown', retryable: true};
  }
};

export const resolveAuthError = (error: unknown, operation: AuthErrorOperation): AuthErrorResolution => {
  if (error instanceof SafeAuthError) {
    return {
      category: error.category, code: error.code, operation, retryable: error.retryable, userMessage: error.message
    };
  }

  if (operation === 'camera') {
    return {
      category: 'unknown', code: 'camera_failure', operation, retryable: true,
      userMessage: AUTH_ERROR_MESSAGES.cameraFailed
    };
  }

  const numericCode = extractNumericErrorCode(error);
  if (numericCode !== null) {
    if (numericCode >= Code.Canceled && numericCode <= Code.Unauthenticated) {
      const connectCode = numericCode as Code;
      const {category, retryable} = classifyConnectCode(connectCode);

      return {
        category, code: connectCodeName(connectCode), operation, retryable,
        userMessage: operationMessage(operation, category)
      };
    }

    const {category, retryable} = classifySmgErrorCode(numericCode, operation);
    return {
      category, code: `server_${numericCode}`, operation, retryable, userMessage: operationMessage(operation, category)
    };
  }

  const connectError = ConnectError.from(error);
  const {category, retryable} = classifyConnectCode(connectError.code);

  return {
    category, code: connectCodeName(connectError.code), operation, retryable,
    userMessage: operationMessage(operation, category)
  };
};

export const normalizeAuthError = (error: unknown, operation: AuthErrorOperation): SafeAuthError => {
  if (error instanceof SafeAuthError) return error;

  const resolution = resolveAuthError(error, operation);
  return new SafeAuthError(resolution);
};

export const authErrorTelemetry = (resolution: AuthErrorResolution): Pick<AuthErrorResolution, 'category' | 'code' | 'operation' | 'retryable'> => ({
  category: resolution.category, code: resolution.code, operation: resolution.operation, retryable: resolution.retryable
});
